import React, { useEffect, useState } from "react";
import dsiImg from "../assets/DSI.png";
import banderaImg from "../assets/bandera.png";
import useReveal from "../hooks/useReveal";

const DETAILS = [
  {
    label: "Facility",
    wide: true,
    value:
      "Lot #2 Otaheite Industrial Park,\nSouth Oropouche, Trinidad, West Indies",
    icon: (
      <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69zM12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
    ),
  },
  {
    label: "Email",
    value: "info@anfieldtt.com",
    href: "mailto:info@anfieldtt.com",
    icon: (
      <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67zM22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
    ),
  },
  {
    label: "Telephone",
    links: [
      { text: "868 474 8903", href: "tel:8684748903" },
      { text: "868 272 1480", href: "tel:8682721480" },
    ],
    icon: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z"
      />
    ),
  },
];

const PARTNERS = [
  {
    name: "Drilling Systems Int’l",
    imageUrl: dsiImg,
    videoUrl: "https://www.youtube.com/embed/I3pA89ipiqs",
  },
  { name: "Bandera Oil Tools", imageUrl: banderaImg },
];

function VideoPopup({ url, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-950/90 p-4 backdrop-blur-md"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Partner video"
    >
      <div
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close video"
          className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center border border-white/20 text-xl text-white transition-colors hover:border-flame-500 hover:text-flame-500"
        >
          ×
        </button>
        <div className="aspect-video w-full border border-white/10">
          <iframe
            src={`${url}?autoplay=1`}
            title="Partner video"
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}

export default function Contact() {
  const [video, setVideo] = useState(null);
  const headRef = useReveal();
  const cardRef = useReveal();

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-ink-950 py-24 lg:py-32"
    >
      <div className="absolute inset-0 bg-grid bg-grid opacity-40" />
      <div className="absolute left-1/2 top-0 h-[28rem] w-[42rem] -translate-x-1/2 rounded-full bg-flame-600/12 blur-[140px]" />

      <div className="relative mx-auto max-w-shell px-6 lg:px-10">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
          {/* Pitch */}
          <div ref={headRef} className="reveal lg:col-span-5">
            <p className="kicker mb-6">05 — Contact</p>
            <h2 className="h-display text-5xl sm:text-6xl lg:text-7xl">
              Talk to a
              <br />
              <span className="text-flame-500">specialist</span>
            </h2>
            <p className="mt-8 max-w-md text-steel-300">
              Tell us what’s stuck, what needs milling, or what you need
              fabricated. Our supervisors will scope the job and get the right
              tools on the truck.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a href="mailto:info@anfieldtt.com" className="btn-flame group">
                Email us
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </a>
              <a href="tel:8684748903" className="btn-ghost">
                868 474 8903
              </a>
            </div>
          </div>

          {/* Detail card */}
          <div ref={cardRef} className="reveal lg:col-span-7">
            <div className="border border-white/10 bg-ink-900/70 backdrop-blur">
              <div className="h-1 bg-hazard opacity-70" />
              <dl className="grid gap-px bg-white/10 sm:grid-cols-2">
                {DETAILS.map((item) => {
                  const content = (
                    <>
                      <span className="mb-3 flex h-9 w-9 items-center justify-center border border-flame-500/40 text-flame-500 transition-colors duration-300 group-hover:bg-flame-500 group-hover:text-white">
                        <svg
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="h-4 w-4"
                          aria-hidden="true"
                        >
                          {item.icon}
                        </svg>
                      </span>
                      <dt className="font-mono text-[10px] uppercase tracking-kicker text-steel-400">
                        {item.label}
                      </dt>
                      <dd className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-white">
                        {item.links ? (
                          <span className="flex flex-col gap-1">
                            {item.links.map((link) => (
                              <a
                                key={link.href}
                                href={link.href}
                                className="w-fit transition-colors duration-300 hover:text-flame-500"
                              >
                                {link.text}
                              </a>
                            ))}
                          </span>
                        ) : (
                          item.value
                        )}
                      </dd>
                    </>
                  );

                  const cell = item.wide ? "sm:col-span-2" : "";

                  return item.href ? (
                    <a
                      key={item.label}
                      href={item.href}
                      className={`group bg-ink-900 p-7 transition-colors duration-300 hover:bg-ink-850 lg:p-8 ${cell}`}
                    >
                      {content}
                    </a>
                  ) : (
                    <div
                      key={item.label}
                      className={`group bg-ink-900 p-7 lg:p-8 ${cell}`}
                    >
                      {content}
                    </div>
                  );
                })}
              </dl>
            </div>

            {/* Partners */}
            <div className="mt-8">
              <p className="mb-5 font-mono text-[11px] uppercase tracking-kicker text-steel-400">
                Our partners
              </p>
              <div className="flex flex-wrap gap-4">
                {PARTNERS.map((partner) => {
                  const isVideo = Boolean(partner.videoUrl);
                  const Tag = isVideo ? "button" : "div";
                  return (
                    <Tag
                      key={partner.name}
                      {...(isVideo
                        ? {
                            type: "button",
                            onClick: () => setVideo(partner.videoUrl),
                            "aria-label": `${partner.name} — play video`,
                          }
                        : {})}
                      className={`group flex items-center gap-4 border border-white/10 bg-ink-900 p-3 pr-6 transition-colors duration-300 ${
                        isVideo ? "cursor-pointer hover:border-flame-500/60" : ""
                      }`}
                    >
                      <span className="flex h-12 w-16 items-center justify-center bg-white p-1.5">
                        <img
                          src={partner.imageUrl}
                          alt={partner.name}
                          className="max-h-full max-w-full object-contain"
                        />
                      </span>
                      <span className="text-left">
                        <span className="block font-mono text-[11px] uppercase tracking-wide2 text-steel-300">
                          {partner.name}
                        </span>
                        {isVideo && (
                          <span className="mt-0.5 block font-mono text-[10px] text-flame-500">
                            ▶ Watch video
                          </span>
                        )}
                      </span>
                    </Tag>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {video && <VideoPopup url={video} onClose={() => setVideo(null)} />}
    </section>
  );
}
